import { DataProvider, fetchUtils } from 'react-admin';

const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:3000';

const httpClient = (url: string, options: fetchUtils.Options = {}) => {
  const token = localStorage.getItem('token');
  const headers = new Headers(options.headers);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return fetchUtils.fetchJson(url, { ...options, headers });
};

export const dataProvider: DataProvider = {
  getList: async (resource, params) => {
    const { page = 1, perPage = 10 } = params.pagination || {};
    const { field = 'id', order = 'ASC' } = params.sort || {};
    const start = (page - 1) * perPage;
    const end = page * perPage;

    const query = new URLSearchParams({
      _start: String(start),
      _end: String(end),
      _sort: field,
      _order: order,
    });

    Object.entries(params.filter || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // Convert date strings to Unix timestamps for history endpoint
        if (resource.includes('history') && (key === 'start' || key === 'end')) {
          const timestamp = Math.floor(new Date(value as string).getTime() / 1000);
          query.append(key, String(timestamp));
        } else {
          query.append(key, String(value));
        }
      }
    });

    const url = `${API_URL}/api/${resource}?${query.toString()}`;
    const { json } = await httpClient(url);

    return {
      data: json.data.map((item: any) => ({ ...item, id: item._id || item.id })),
      total: json.total,
    };
  },

  getOne: async (resource, params) => {
    const url = `${API_URL}/api/${resource}/${params.id}`;
    const { json } = await httpClient(url);
    return { data: { ...json, id: json._id || json.id } };
  },

  getMany: async (resource, params) => {
    const results = await Promise.all(
      params.ids.map((id) =>
        httpClient(`${API_URL}/api/${resource}/${id}`).then(({ json }) => ({
          ...json,
          id: json._id || json.id,
        }))
      )
    );
    return { data: results };
  },

  getManyReference: async (resource, params) => {
    const { page = 1, perPage = 10 } = params.pagination || {};
    const { field = 'id', order = 'ASC' } = params.sort || {};
    const start = (page - 1) * perPage;
    const end = page * perPage;

    const query = new URLSearchParams({
      _start: String(start),
      _end: String(end),
      _sort: field,
      _order: order,
      [params.target]: String(params.id),
    });

    const url = `${API_URL}/api/${resource}?${query.toString()}`;
    const { json } = await httpClient(url);

    return {
      data: json.data.map((item: any) => ({ ...item, id: item._id || item.id })),
      total: json.total,
    };
  },

  update: async (resource, params) => {
    const url = `${API_URL}/api/${resource}/${params.id}`;
    const { json } = await httpClient(url, {
      method: 'PUT',
      body: JSON.stringify(params.data),
    });
    return { data: { ...json, id: json._id || json.id } };
  },

  updateMany: async (resource, params) => {
    await Promise.all(
      params.ids.map((id) =>
        httpClient(`${API_URL}/api/${resource}/${id}`, {
          method: 'PUT',
          body: JSON.stringify(params.data),
        })
      )
    );
    return { data: params.ids };
  },

  create: async (resource, params) => {
    const url = `${API_URL}/api/${resource}`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify(params.data),
    });
    return { data: { ...json, id: json._id || json.id } };
  },

  delete: async (resource, params) => {
    const url = `${API_URL}/api/${resource}/${params.id}`;
    const { json } = await httpClient(url, {
      method: 'DELETE',
    });
    return { data: { ...json, id: params.id } };
  },

  deleteMany: async (resource, params) => {
    await Promise.all(
      params.ids.map((id) =>
        httpClient(`${API_URL}/api/${resource}/${id}`, {
          method: 'DELETE',
        })
      )
    );
    return { data: params.ids };
  },
};
